import { useState, useEffect } from "react";
import { Checkbox } from "primereact/checkbox";
import { RadioButton } from "primereact/radiobutton";
import { Tooltip } from "primereact/tooltip";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { InputSwitch } from "primereact/inputswitch";
import { FloatLabel } from "primereact/floatlabel";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestionCircle } from "@fortawesome/pro-regular-svg-icons";
import AssessmentDateMode from "@/components/assignments/AssessmentDateMode";
import { ValidationMessage } from "sj-ui-toolkit/src/components";
import { useStrictKnowledgeAssessment } from "sj-ui-toolkit/src/hooks/index";
import type { FormSettings } from "@/api/assignments";
import { normalizeNotificationsFrequency } from "@/helpers/assignmentHelpers";

// ─── Date helpers ─────────────────────────────────────────────────────────────

const findDaysBetween = (a: Date, b: Date): number => {
  const msPerDay = 1000 * 60 * 60 * 24;
  const start = new Date(a);
  start.setHours(0, 0, 0, 0);
  const end = new Date(b);
  end.setHours(0, 0, 0, 0);
  if (end < start) return 0;
  // +1 for inclusive counting (same as admin-v2's findDaysBetween)
  return Math.round((end.getTime() - start.getTime()) / msPerDay) + 1;
};

const setToEndOfDay = (date: Date | number): Date => {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
};

// ─── Reminder text map ────────────────────────────────────────────────────────

const reminderTextMap: Record<string, string> = {
  None: "No reminder emails will be sent.",
  Daily: "Learners will receive reminder emails every day until the assessment is complete.",
  Weekly: "Learners will receive reminder emails every week until the assessment is complete.",
  Monthly: "Learners will receive reminder emails every month until the assessment is complete.",
  Quarterly:
    "Learners will receive reminder emails every quarter until the assessment is complete.",
};

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SettingsValidationErrors {
  name?: { hasError?: boolean; message?: string };
  description?: { hasError?: boolean; message?: string };
  date?: { hasError?: boolean; message?: string };
}

// ─── Component ───────────────────────────────────────────────────────────────

interface AssignmentSettingsProps {
  values: FormSettings;
  handleChange: (values: FormSettings) => void;
  showValidationMessages?: boolean;
  validationErrors?: SettingsValidationErrors;
}

const notificationOptions = ["none", "daily", "weekly", "monthly", "quarterly"];
const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const AssignmentSettings = ({
  values = {},
  handleChange,
  showValidationMessages = false,
  validationErrors = {},
}: AssignmentSettingsProps) => {
  const [endDate, setEndDate] = useState<Date | null>(
    values.endDate ? new Date(values.endDate as Date) : null
  );
  const [strictAssignment, setStrictAssignment] = useState(!!values.isRequired);
  const [sendReminders, setSendReminders] = useState(
    normalizeNotificationsFrequency(values.sendReminders)
  );
  const [daysBetween, setDaysBetween] = useState(
    values.dates?.dynamic && values.dates?.dueAfter?.days
      ? values.dates.dueAfter.days
      : endDate
        ? findDaysBetween(new Date(), endDate)
        : 0
  );

  const strictKnowledgeAssessmentsEnabled = useStrictKnowledgeAssessment();

  // Sync endDate from props when it arrives asynchronously (edit mode async load).
  useEffect(() => {
    setEndDate(values.endDate ? new Date(values.endDate as Date) : null);
  }, [values.endDate]);

  // Sync sendReminders from props when it arrives asynchronously (edit mode async load).
  // Normalise to lowercase so radio buttons ("none", "daily", …) match regardless
  // of what casing the Rails API returns.
  useEffect(() => {
    if (typeof values.sendReminders === "string") {
      setSendReminders(normalizeNotificationsFrequency(values.sendReminders));
    }
  }, [values.sendReminders]);

  useEffect(() => {
    if (values.dates?.dynamic) return;
    const days = endDate ? findDaysBetween(new Date(), endDate) : 0;
    setDaysBetween(days);
  }, [endDate, values.dates?.dynamic]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleChange({ ...values, name: e.target.value } as FormSettings);
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    handleChange({ ...values, description: e.target.value } as FormSettings);
  };

  const handleActivatedChange = (e: { value: boolean }) => {
    handleChange({ ...values, activated: e.value } as FormSettings);
  };

  const handleEndDateChange = (e: { value: Date | null }) => {
    const newEndDate = e.value ? setToEndOfDay(e.value) : null;
    setEndDate(newEndDate);
    const days = newEndDate ? findDaysBetween(new Date(), newEndDate) : 0;
    setDaysBetween(days);
    handleChange({
      ...values,
      endDate: newEndDate,
      dates: {
        ...values.dates,
        dueAfter: { ...values.dates?.dueAfter, days },
      },
    } as FormSettings);
  };

  const toggleDynamicMode = (isDynamic: boolean) => {
    const days =
      isDynamic && !values.dates?.dueAfter?.days
        ? daysBetween || 0
        : (values.dates?.dueAfter?.days ?? 0);
    handleChange({
      ...values,
      dates: {
        ...values.dates,
        dynamic: isDynamic,
        dueAfter: isDynamic ? { ...values.dates?.dueAfter, days } : values.dates?.dueAfter,
      },
    } as FormSettings);
  };

  const onStrictAssignmentChange = (e: { checked?: boolean }) => {
    setStrictAssignment(!!e.checked);
  };

  const onSendRemindersChange = (value: string) => {
    setSendReminders(value);
    handleChange({ ...values, sendReminders: value } as FormSettings);
  };

  const name = values.name ?? "";
  const description = (values as FormSettings & { description?: string }).description ?? "";

  const isNameEmpty = !name || name.trim().length === 0;
  const hasNameError = !!validationErrors?.name?.hasError;
  const isNameInvalid = isNameEmpty || hasNameError;
  const showNameError =
    hasNameError &&
    validationErrors.name?.message &&
    (validationErrors.name.message.includes("already exists") || showValidationMessages);

  const isDescriptionEmpty = !description || description.trim().length === 0;
  const isDescriptionInvalid =
    isDescriptionEmpty || (showValidationMessages && !!validationErrors?.description?.hasError);
  const showDescriptionError = showValidationMessages && !!validationErrors?.description?.hasError;

  return (
    <div className="p-4">
      <div className="mb-8">
        <FloatLabel>
          <label htmlFor="assignment-name" className="block mb-2">
            Assignment Name*
          </label>
          <InputText
            id="assignment-name"
            data-testid="assignment-name"
            type="text"
            autoComplete="off"
            value={name}
            onChange={handleNameChange}
            className="w-full"
            aria-required="true"
            aria-invalid={isNameInvalid}
            aria-describedby={hasNameError ? "assignment-name-error" : undefined}
            invalid={isNameEmpty}
            placeholder="Enter assignment name"
          />
        </FloatLabel>
        {showNameError ? (
          <div role="alert">
            <ValidationMessage
              severity="error"
              text={validationErrors.name?.message ?? ""}
              id="assignment-name-error"
            />
          </div>
        ) : null}
      </div>

      <div className="mb-4">
        <FloatLabel>
          <label htmlFor="assignment-description" className="block mb-2">
            Assignment Description*
          </label>
          <InputTextarea
            id="assignment-description"
            data-testid="assignment-description"
            value={description}
            onChange={handleDescriptionChange}
            rows={5}
            className="w-full"
            aria-required="true"
            aria-invalid={isDescriptionInvalid}
            aria-describedby={showDescriptionError ? "assignment-description-error" : undefined}
            invalid={isDescriptionEmpty}
            placeholder="Enter a description for this assignment"
          />
        </FloatLabel>
        {showDescriptionError && (
          <div role="alert">
            <ValidationMessage
              severity="error"
              text={validationErrors.description?.message ?? ""}
              id="assignment-description-error"
            />
          </div>
        )}
      </div>

      <div className="mb-4">
        <p className="mb-2">Assignment Status:</p>
        <div className="flex items-center gap-2">
          <InputSwitch
            id="activated"
            data-testid="activated-switch"
            checked={values.activated !== false}
            onChange={handleActivatedChange}
            className="scale-75"
          />
          <label htmlFor="activated">Activate Assignment</label>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-3">
        <div>
          <AssessmentDateMode
            startDate={new Date()}
            endDate={endDate}
            daysBetween={daysBetween}
            handleEndDateChange={handleEndDateChange}
            toggleDynamicMode={toggleDynamicMode}
            isDynamic={values.dates?.dynamic}
          />
          {showValidationMessages && validationErrors?.date?.hasError && (
            <div role="alert">
              <ValidationMessage
                severity="error"
                text={validationErrors.date?.message ?? ""}
                id="assignment-date-error"
              />
            </div>
          )}
        </div>

        <div>
          <div className="mb-4">
            <p>Notify Learners:</p>
            <div className="mt-2">
              <p className="body-sm">
                Learners will get notified as soon as the assignment is created.
              </p>
            </div>
          </div>
          <div>
            <p>Send reminders:</p>
            {notificationOptions.map(option => (
              <div key={option} className="mt-2 flex items-center gap-2">
                <RadioButton
                  inputId={`reminder-${option}`}
                  name="sendReminders"
                  value={option}
                  onChange={() => onSendRemindersChange(option)}
                  checked={sendReminders === option}
                />
                <label htmlFor={`reminder-${option}`}>{capitalize(option)}</label>
                <FontAwesomeIcon
                  id={`notification-tooltip-${option}`}
                  icon={faQuestionCircle}
                  size="sm"
                  style={{ marginLeft: "0.1rem", cursor: "pointer" }}
                />
                <Tooltip target={`#notification-tooltip-${option}`}>
                  {reminderTextMap[capitalize(option)]}
                </Tooltip>
              </div>
            ))}
          </div>
          {strictKnowledgeAssessmentsEnabled && (
            <div className="mt-4">
              <p>Strict Assignment:</p>
              <div className="flex items-center gap-2 mt-2">
                <Checkbox
                  inputId="strict-assignment"
                  onChange={onStrictAssignmentChange}
                  checked={strictAssignment}
                />
                <label htmlFor="strict-assignment">
                  Lock all other content to users until this assignment is completed.
                </label>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssignmentSettings;
